import { useState } from "react";
import { presalesApi } from "../../api/presalesApi";

const TODAY = new Date().toISOString().slice(0, 10);

function daysBetween(a, b) {
  return Math.round((new Date(a) - new Date(b)) / 86400000);
}

function rmdClass(rmd) {
  if (effectiveDone(rmd)) return "done";
  const diff = daysBetween(rmd.due_date, TODAY);
  if (diff < 0) return "over";
  if (diff <= 2) return "due";
  return "";
}

function isOverdue(rmd) {
  return daysBetween(rmd.due_date, TODAY) < 0;
}

function effectiveDone(rmd) {
  return !!rmd.is_done || isOverdue(rmd);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")} ${d.toLocaleString("en", { month: "short" })}`;
}

function dueText(rmd) {
  const diff = daysBetween(rmd.due_date, TODAY);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  return `in ${diff}d`;
}

export default function RemindersTab({ lead, mutate, showToast }) {
  const reminders = lead.reminders || [];
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", due_date: "" });
  const [saving, setSaving] = useState(false);

  async function toggle(rmd) {
    try {
      await presalesApi.patchReminder(rmd.id, { is_done: !rmd.is_done });
      await mutate();
    } catch (err) {
      showToast?.(err.message || "Failed to update reminder", "error");
    }
  }

  async function addReminder() {
    if (!form.title || !form.due_date) return;
    setSaving(true);
    try {
      await presalesApi.addReminder(lead.id, form);
      setForm({ title: "", due_date: "" });
      setAdding(false);
      await mutate();
      showToast?.("Reminder added", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to add reminder", "error");
    } finally {
      setSaving(false);
    }
  }

  async function deleteReminder(rmdId) {
    try {
      await presalesApi.deleteReminder(rmdId);
      await mutate();
    } catch (err) {
      showToast?.(err.message || "Failed to delete", "error");
    }
  }

  const open   = reminders.filter((r) => !effectiveDone(r));
  const done   = reminders.filter((r) => effectiveDone(r));
  const sorted = [...open, ...done];

  return (
    <div className="ps-so-section">
      <div className="ps-so-lbl">
        <span>Reminders</span>
        <button onClick={() => setAdding((v) => !v)}>+ Add</button>
      </div>

      {adding && (
        <div className="ps-add-row" style={{ marginBottom: 12 }}>
          <input
            type="text"
            className="ps-add-input"
            placeholder="Reminder title…"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
          <input
            type="date"
            className="ps-add-input"
            style={{ flex: "0 0 140px" }}
            value={form.due_date}
            onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
          />
          <button
            className="ps-add-btn"
            disabled={!form.title || !form.due_date || saving}
            onClick={addReminder}
          >
            {saving ? "…" : "Add"}
          </button>
          <button className="ps-add-cancel-btn" onClick={() => setAdding(false)}>
            Cancel
          </button>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="ps-so-empty">No reminders</div>
      ) : (
        <div className="ps-rmd-list">
          {sorted.map((rmd) => (
            <div key={rmd.id} className={`ps-rmd ${rmdClass(rmd)}`}>
              <input
                type="checkbox"
                checked={effectiveDone(rmd)}
                onChange={() => toggle(rmd)}
                disabled={isOverdue(rmd) && !rmd.is_done}
              />
              <span className="t">{rmd.title}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span className="d">{fmtDate(rmd.due_date)} · {dueText(rmd)}</span>
                <button className="ps-rmd-del" onClick={() => deleteReminder(rmd.id)}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
